/**
 * MarketingPage — the marketing page renderer. A page is a declared
 * `MarketingPageSpec`: a persistent `header`, an optional `tabs` body, and an
 * optional persistent `footer`. The renderer is dumb — it renders exactly what
 * the manifest declares (no section-guessing, no "is this a tab" heuristics).
 *
 *   header  → hero + content bands (persistent, above the tabs)
 *   tabs    → sticky, URL-persistent tab group (the body)
 *   footer  → content bands below the tabs (persistent, e.g. a closing CTA)
 *
 * Section backgrounds + vertical rhythm come from the shared design tokens
 * (--section-bg-*, --section-padding-y) — not literals here.
 *
 * Rule 33: this renderer + its block primitives live in @inferrex/ui. Page
 * files are codegen-emitted from manifests; new block types are added HERE.
 */
import { useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from './button';
import { BLOCK_REGISTRY, WIDE_BLOCKS, type MarketingBlock } from './block-registry';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './tabs';
import { cn } from '../utils';

export interface MarketingTab {
  readonly label: string;
  readonly blocks: readonly MarketingBlock[];
}

/**
 * A declared marketing page: persistent header, optional tabbed body, optional
 * persistent footer. The single source a page manifest exports.
 */
export interface MarketingPageSpec {
  readonly header?: readonly MarketingBlock[];
  readonly tabs?: readonly MarketingTab[];
  readonly footer?: readonly MarketingBlock[];
}

// `MarketingBlock` + `WIDE_BLOCKS` are derived from the BLOCK_REGISTRY manifest
// (./block-registry), which the generated primitives barrel now surfaces
// directly — so `@inferrex/ui` consumers still import the block union from one
// place (`@inferrex/ui`), without this file double-exporting it.

/**
 * Generic block dispatcher — looks the block's primitive up in BLOCK_REGISTRY
 * and renders it. No per-type switch: adding a block type is ONE entry in the
 * registry manifest + its `@ix:` marker in a /docs/marketing doc. `recursive`
 * blocks (e.g. prose-section) render their `body` into the primitive as children.
 */
function renderBlock(block: MarketingBlock, key: number): ReactNode {
  const def = BLOCK_REGISTRY[block.type];
  if (!def) return null;
  const Component = def.component;
  if (def.recursive) {
    const { type: _t, body, ...p } = block as { type: string; body?: readonly MarketingBlock[] } & Record<string, unknown>;
    return <Component key={key} {...p}>{(body ?? []).map((b, i) => renderBlock(b, i))}</Component>;
  }
  const { type: _t, ...p } = block as { type: string } & Record<string, unknown>;
  return <Component key={key} {...p} />;
}

/** Wrap a non-hero block in its width lane within a band. */
function laned(block: MarketingBlock, key: number): ReactNode {
  // A block reaches the full content width either by its type (WIDE_BLOCKS) or
  // per-instance via `wide: true` — e.g. the generated API-reference tables, which
  // are data-dense and should use the page, not the ~75ch reading measure.
  if (WIDE_BLOCKS.has(block.type) || (block as { readonly wide?: boolean }).wide) return renderBlock(block, key);
  // Running prose centres on the reading measure (~75 chars) so header/footer
  // bands match the in-card measure; other lane blocks keep the wider 3xl lane.
  const lane = block.type === 'prose' ? 'max-w-(--reading-measure)' : 'max-w-3xl';
  return (
    <div key={key} className={cn('mx-auto', lane)}>
      {renderBlock(block, key)}
    </div>
  );
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'tab';
}

/**
 * Persistent, sticky tab group. The active tab is synced to the `?tab=` URL
 * param so it survives reload, is shareable/deep-linkable, and works with the
 * back button. The tab bar sticks to the top of the viewport while scrolling.
 */
function MarketingTabs({ tabs }: { tabs: readonly MarketingTab[] }) {
  const [params, setParams] = useSearchParams();
  const seen = new Map<string, number>();
  const values = tabs.map((t) => {
    const base = slugify(t.label);
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}-${n + 1}`;
  });
  const requested = params.get('tab');
  const active = requested && values.includes(requested) ? requested : values[0]!;

  // Non-sticky anchor at the tabs' natural top. We scroll THIS (not the sticky
  // bar, whose scrollIntoView is a no-op once it's pinned at the viewport top).
  const rootRef = useRef<HTMLDivElement>(null);

  const onChange = (v: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', v);
    setParams(next, { replace: true });
  };

  // Advance to the next tab and scroll back to the top of the tab body, so a
  // reader at the foot of a long tab starts the next one from its beginning.
  const goNext = (i: number) => {
    onChange(values[i + 1]!);
    requestAnimationFrame(() => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  return (
    <Tabs ref={rootRef} value={active} onValueChange={onChange} className="w-full">
      <div className="sticky top-0 z-20 -mx-6 border-b border-border bg-background/95 px-6 py-2.5 backdrop-blur">
        {/* Mobile: a single-row horizontal scroll strip (swipe through tabs) so a
            many-tab page (e.g. /about's 12 tabs) doesn't wrap into a 3-row sticky
            header that eats a 390px viewport. Wraps normally from sm up. Scrollbar
            hidden for a clean strip; this overflow-x-auto is its own scroll
            container, independent of the page-level mobile clip backstop. */}
        <TabsList
          variant="pill"
          className="flex flex-nowrap gap-(--tabs-pill-gap) overflow-x-auto [scrollbar-width:none] sm:flex-wrap sm:overflow-x-visible [&::-webkit-scrollbar]:hidden"
        >
          {tabs.map((t, i) => <TabsTrigger key={i} value={values[i]!} className="shrink-0">{t.label}</TabsTrigger>)}
        </TabsList>
      </div>
      {tabs.map((t, i) => (
        // forceMount keeps every panel in the DOM so prerendered HTML carries the
        // whole page's content (crawlers otherwise only ever see tab 1); the
        // data-state guard hides inactive panels visually.
        <TabsContent key={i} value={values[i]!} forceMount className="pt-(--space-block) data-[state=inactive]:hidden">
          <div className="space-y-(--space-block)">
            {t.blocks.some((b) => b.type === 'prose-section')
              ? t.blocks.map((b, j) => renderBlock(b, j))
              : t.blocks.map((b, j) => laned(b, j))}
          </div>
          {i < tabs.length - 1 && (
            <div className="mt-(--space-block) flex justify-end border-t border-border pt-(--space-6)">
              <Button variant="outline" onClick={() => goNext(i)}>
                Next: {tabs[i + 1]!.label}
                <ChevronRight className="size-4" aria-hidden="true" />
              </Button>
            </div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
}

interface Section {
  readonly hero: boolean;
  readonly blocks: { block: MarketingBlock; index: number }[];
}

/** Group a block list into sections delimited by hero / section-header. */
function toSections(blocks: readonly MarketingBlock[]): Section[] {
  const sections: Section[] = [];
  blocks.forEach((block, index) => {
    const last = sections[sections.length - 1];
    if (block.type === 'hero') {
      sections.push({ hero: true, blocks: [{ block, index }] });
    } else if (block.type === 'section-header' || !last || last.hero) {
      sections.push({ hero: false, blocks: [{ block, index }] });
    } else {
      last.blocks.push({ block, index });
    }
  });
  return sections;
}

/** Render-state shared while emitting a page's bands (alternation + first-band). */
interface BandState {
  bandIndex: number;
  firstBand: boolean;
}

/** Render a block list as hero + content bands; styling from shared tokens. */
function renderSections(blocks: readonly MarketingBlock[], st: BandState, keyPrefix: string): ReactNode[] {
  return toSections(blocks).map((section, s) => {
    if (section.hero) {
      const { block, index } = section.blocks[0]!;
      return renderBlock(block, index);
    }
    // A section-header may opt into the accent band (--section-bg-accent) to
    // break the primary/secondary rhythm — manifest-driven via @ix:section bg="accent".
    const lead = section.blocks[0]?.block;
    const accent = lead?.type === 'section-header' && lead.bg === 'accent';
    const tinted = st.bandIndex % 2 === 1;
    st.bandIndex += 1;
    const topTight = st.firstBand;
    st.firstBand = false;
    return (
      <section key={`${keyPrefix}-${s}`} className={cn('w-full border-b border-border/50', accent ? 'bg-section-accent' : tinted ? 'bg-section-secondary' : 'bg-section-primary')}>
        <div className={cn('mx-auto max-w-6xl space-y-(--space-block) px-6 pb-(--section-padding-y)', topTight ? 'pt-(--space-block)' : 'pt-(--section-padding-y)')}>
          {section.blocks.map(({ block, index }) => laned(block, index))}
        </div>
      </section>
    );
  });
}

export interface MarketingPageProps {
  readonly spec: MarketingPageSpec;
}

export function MarketingPage({ spec }: MarketingPageProps) {
  const st: BandState = { bandIndex: 0, firstBand: true };
  const header = renderSections(spec.header ?? [], st, 'h');
  // The tab bar starts firstBand for the footer too (it provides the separation).
  st.firstBand = true;
  const tabs = spec.tabs && spec.tabs.length > 0 ? spec.tabs : undefined;
  const footer = renderSections(spec.footer ?? [], st, 'f');

  return (
    <article>
      {header}
      {tabs && (
        <section className="w-full">
          <div className="mx-auto max-w-6xl px-6 pb-(--section-padding-y)">
            <MarketingTabs tabs={tabs} />
          </div>
        </section>
      )}
      {footer}
    </article>
  );
}
